import scores from './provider.scores.json'

// ---------------------------------------------------------------------------
// The scenario class used to select the scenario for bewertung and empfeflung
// ---------------------------------------------------------------------------
export class Scenario {
  constructor( drug, riskPoints, risks, riskPointsUnknown, risksUnknown ) {
    this.drug = drug
    this.riskPoints = riskPoints
    this.risks = risks
    this.nonDrugRisks = risks.filter( risk => typeof risk.drug === 'undefined' )
    this.riskPointsUnknown = riskPointsUnknown
    this.risksUnknown = risksUnknown
    this.elektrolytRisks = risks.filter( rf => rf.group === 'elektrolyt' )
    this.isElektrolytInRisks = this.elektrolytRisks.length > 0
    this.pointsWithoutElektrolyts = this._pointsWithoutElektrolyts()
    this.qtcRisk = risks.filter( risk => risk.id === 'qtc' )[ 0 ]
    this.isQTCInRisks = typeof qtcRisk !== 'undefined'
    this.qtcIsUnknown = this.isQTCInRisks && this.qtcRisk.unknown
  }
  _pointsWithoutElektrolyts() {
    let pointsWithoutElektrolyts = this.riskPoints
    for ( const rf of this.elektrolytRisks ) {
      // Remove the points associated with elektrolyts
      pointsWithoutElektrolyts -= scores[ rf.group ][ rf.id ].score
    }
    return pointsWithoutElektrolyts
  }

  // --------------------------------------------------
  // Construct the scenario name from the template name
  // --------------------------------------------------
  _constructScenarioName( template ) {

    //            non-drug risks yes/no              //
    // ------------------------------------------------
    if ( this.nonDrugRisks.length === 0 ) {
      // no non-drug risks
      template = template.replace( '_rf_', '_no_RF_' )
    } else {
      // non-drug risks
      template = template.replace( '_rf_', '_RF_' )
    }
    // ------------------------------------------------

    //            Low vs Normal Elektrolyt           //
    // ------------------------------------------------
    if ( this.isElektrolytInRisks && this.pointsWithoutElektrolyts < 10 ) {
      // hypo-kaliamie/calziamie relevant for score being >= 10
      template = template.replace( '_value_elektrolyt_', '_low_elektrolyt_' )
    } else {
      // normal kalium/calcium levels or hypo-kaliamie/calziamie not relevant
      // for score being >= 10
      template = template.replace( '_value_elektrolyt_', '_normal_elektrolyt_' )
    }
    // ------------------------------------------------

    //            QTC unknown/normal/prolonged       //
    // ------------------------------------------------
    if ( this.isQTCInRisks ) {
      if ( this.qtcIsUnknown ) {
        // unknown qtc
        template = template.replace( '_value_qtc_', '_unknown_qtc_' )
      } else {
        // prolonged qtc
        template = template.replace( '_value_qtc_', '_prolonged_qtc_' )
      }
    } else {
      // normal qtc
      template = template.replace( '_value_qtc_', '_normal_qtc_' )
    }
    // ------------------------------------------------

    //            Single/multiple unknown risks      //
    // ------------------------------------------------
    if ( this.risksUnknown.length === 1 ) {
      template = template.replace( '_unknown_count_', '_single_' )
    } else {
      template = template.replace( '_unknown_count_', '_multi_' )
    }
    // ------------------------------------------------

    return template
  }

  // -----------------------------------------------------------------
  // Selects the risk scenario for choosing the appropriate texts for
  // Bewertung and Empfehlung chapters
  // -----------------------------------------------------------------
  selectScenario() {
    // -----------------------------------
    // Risk score >= 10 - CONTRAINDICATED
    // -----------------------------------
    // Without unknown risks
    if ( this.riskPoints >= 10 && this.riskPointsUnknown === 0 ) {
      return this._scenariosContraindicated()
    }
    // With unknown risks
    else if ( this.riskPoints >= 10 && this.riskPointsUnknown > 0 ) {
      return this._scenariosContraindicatedUnknown()
    }
    // -----------------------------
    // Risk score < 10 - MONITORING
    // -----------------------------
    // Without unknown risks
    else if ( this.riskPoints < 10 && this.riskPointsUnknown === 0 ) {
      return this._scenariosMonitoring()
    }
    // With unknown risks
    else if ( this.riskPoints < 10 && this.riskPoints + this.riskPointsUnknown < 10 ) {
      return this._scenariosMonitoringUnknown()
    }

    // -----------------------------------------------------------------------
    // Risk score < 10 but >= 10 with unknwon risks MONITORING/CONTRAINDICATED
    // -----------------------------------------------------------------------
    else if ( this.riskPoints < 10 && this.riskPoints + this.riskPointsUnknown >= 10 ) {
      return this._scenariosMonitoringContraindicatedUnknown()
    }
  }

  // ----------------------------------------------------------------------------
  // !!! Scenarios for RiskPoints >= 10 and no unknown risk (Contraindicated) !!!
  // ----------------------------------------------------------------------------
  _scenariosContraindicated() {
    // ---------------------------------------------------------------------
    // Bewertung possible scenarios:
    //    mt10_RF_bewertung,
    //    mt10_no_RF_bewertung
    // Empfehlung possible scenarios:
    //    mt10_normal_elektrolyt_empfehlung,
    //    mt10_low_elektrolyt_empfehlung
    // ---------------------------------------------------------------------
    const bewertungText = this._constructScenarioName( 'mt10_rf_bewertung' )
    const empfehlungText = this._constructScenarioName( 'mt10_value_elektrolyt_empfehlung' )
    return { bewertungText, empfehlungText }
  }

  // ----------------------------------------------------------------------
  // !!! Scenarios for RiskPoints < 10 and no unknown risk (Monitoring) !!!
  // ----------------------------------------------------------------------
  _scenariosMonitoring() {
    // ---------------------------------------------------------------------
    // Bewertung possible scenarios:
    //    wt10_no_RF_normal_qtc_normal_elektrolyt_bewertung,
    //    wt10_RF_normal_qtc_normal_elektrolyt_bewertung,
    //    wt10_RF_unknown_qtc_normal_elektrolyt_bewertung,
    //    wt10_RF_prolonged_qtc_normal_elektrolyt_bewertung,
    //    wt10_RF_normal_qtc_low_elektrolyt_bewertung,
    //    wt10_RF_prolonged_qtc_low_elektrolyt_bewertung
    // Empfehlung possible scenarios:
    //    wt10_unknown_qtc_low_elektrolyt_empfehlung,
    //    wt10_prolonged_qtc_low_elektrolyt_empfehlung,
    //    wt10_normal_qtc_low_elektrolyt_empfehlung,
    //    wt10_unknown_qtc_normal_elektrolyt_empfehlung,
    //    wt10_prolonged_qtc_normal_elektrolyt_empfehlung,
    //    wt10_normal_qtc_normal_elektrolyt_empfehlung
    // ---------------------------------------------------------------------
    const bewertungText = this._constructScenarioName( 'wt10_rf_value_qtc_value_elektrolyt_bewertung' )
    const empfehlungText = this._constructScenarioName( 'wt10_value_qtc_value_elektrolyt_empfehlung' )
    return { bewertungText, empfehlungText }
  }

  // ----------------------------------------------------------------------------
  // !!! Scenarios for RiskPoints >= 10 with unknown risk (Contraindicated) !!!
  // ----------------------------------------------------------------------------
  _scenariosContraindicatedUnknown() {
    // ---------------------------------------------------------------------
    // Bewertung possible scenarios:
    //    mt10_RF_single_unknown_bewertung,
    //    mt10_RF_multi_unknown_bewertung,
    //    mt10_no_RF_single_unknown_bewertung
    //    mt10_no_RF_multi_unknown_bewertung
    // Empfehlung possible scenarios:
    //    mt10_normal_elektrolyt_empfehlung
    // ---------------------------------------------------------------------
    const bewertungText = this._constructScenarioName( 'mt10_rf_unknown_count_unknown_bewertung' )
    const empfehlungText = this._constructScenarioName( 'mt10_normal_elektrolyt_empfehlung' )
    return { bewertungText, empfehlungText }
  }

  // -------------------------------------------------------------------------
  // !!! Scenarios for RiskPoints < 10 even with unknown risk (Monitoring) !!!
  // -------------------------------------------------------------------------
  _scenariosMonitoringUnknown() {
    // ---------------------------------------------------------------------
    // Bewertung possible scenarios:
    //    wt10_RF_single_unknown_bewertung,
    //    wt10_RF_multi_unknown_bewertung,
    //    wt10_no_RF_single_unknown_bewertung
    //    wt10_no_RF_multi_unknown_bewertung
    // Empfehlung possible scenarios:
    //    wt10_normal_qtc_normal_elektrolyt_empfehlung
    // ---------------------------------------------------------------------
    const bewertungText = this._constructScenarioName( 'wt10_rf_unknown_count_unknown_bewertung' )
    const empfehlungText = this._constructScenarioName( 'wt10_normal_qtc_normal_elektrolyt_empfehlung' )
    return { bewertungText, empfehlungText }
  }

  // ---------------------------------------------------------------------------------------------
  // !!! Scenarios for RiskPoints < 10 but > 10 with unknown risk (Monitoring/Contraindicated) !!!
  // ---------------------------------------------------------------------------------------------
  _scenariosMonitoringContraindicatedUnknown() {
    const bewertungText = this._constructScenarioName( 'wt10_mt10_rf_unknown_count_unknown_bewertung' )
    const empfehlungText = this._constructScenarioName( 'wt10_mt10_unknown_empfehlung' )
    return { bewertungText, empfehlungText }
  }
}